import type { StatusIndicatorProps } from "@/components/dashboard/DashboardStatusIndicator";

// ── Priority levels (1 = most critical) ──────────────

export type AlertPriority = 1 | 2 | 3 | 4 | 5;

export interface PrioritizedAlert {
  priority: AlertPriority;
  category: "health" | "overload" | "incoherence" | "missing_data" | "optimization";
  status: "ok" | "incomplete" | "overload" | "chaos" | "unknown";
  label: string;
  message: string;
  /** Additional secondary alerts (not displayed but tracked) */
  silenced: SilencedAlert[];
}

export interface SilencedAlert {
  priority: AlertPriority;
  category: PrioritizedAlert["category"];
  message: string;
}

// ── Alert detection functions ────────────────────────

interface DetectedAlert {
  priority: AlertPriority;
  category: PrioritizedAlert["category"];
  status: PrioritizedAlert["status"];
  label: string;
  message: string;
}

function detectAlerts(props: StatusIndicatorProps): DetectedAlert[] {
  const alerts: DetectedAlert[] = [];
  const tab = props.activeTab;

  // ── P1: Sécurité / Blessure / Santé ──
  if (props.recentPainDays >= 2) {
    const hasIntenseTraining =
      props.consecutiveTrainingDays >= 3 || (props.fatigueScore ?? 0) >= 5;
    alerts.push({
      priority: 1,
      category: "health",
      status: "overload",
      label: "Risque blessure",
      message: hasIntenseTraining
        ? "Douleurs récurrentes avec charge active — réduire l'intensité"
        : "Douleurs signalées sur plusieurs jours — surveiller",
    });
  } else if (props.recentPainDays === 1) {
    alerts.push({
      priority: 1,
      category: "health",
      status: "chaos",
      label: "Douleur signalée",
      message: "Douleur récente — adapter la charge si nécessaire",
    });
  }

  // ── P2: Surcharge physiologique ──
  if ((props.fatigueScore ?? 0) >= 7) {
    alerts.push({
      priority: 2,
      category: "overload",
      status: "overload",
      label: "Fatigue élevée",
      message: `Fatigue ${props.fatigueScore?.toFixed(0)}/10 — réduire la charge ou prendre un jour de repos`,
    });
  } else if ((props.fatigueScore ?? 0) >= 5) {
    alerts.push({
      priority: 2,
      category: "overload",
      status: "overload",
      label: "Fatigue accumulée",
      message: "Fatigue accumulée — surveiller la récupération",
    });
  }

  if (props.consecutiveTrainingDays >= 5) {
    alerts.push({
      priority: 2,
      category: "overload",
      status: "overload",
      label: "Récupération insuffisante",
      message: `${props.consecutiveTrainingDays} jours consécutifs sans repos — prévoir une journée off`,
    });
  } else if (props.consecutiveTrainingDays >= 3) {
    alerts.push({
      priority: 2,
      category: "overload",
      status: "chaos",
      label: "Charge élevée",
      message: `${props.consecutiveTrainingDays} jours d'entraînement consécutifs — surveiller`,
    });
  }

  // ── P3: Incohérence programme / planning ──
  if (props.hasChaos || props.isOverride) {
    alerts.push({
      priority: 3,
      category: "incoherence",
      status: "chaos",
      label: "Semaine perturbée",
      message: props.isOverride
        ? "Semaine exceptionnelle — ajuster les créneaux"
        : "Perturbation détectée — adapter le planning",
    });
  }

  if (props.hasPlanning && !props.programTitle && !props.freeMode) {
    alerts.push({
      priority: 3,
      category: "incoherence",
      status: "incomplete",
      label: "Programme manquant",
      message: "Planning actif sans programme associé — en créer ou en activer un",
    });
  }

  // Missed sessions: only remind at end of week (Friday-Sunday)
  // to let the user validate throughout the week without nagging
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0=Sun, 5=Fri, 6=Sat
  const isEndOfWeek = dayOfWeek === 0 || dayOfWeek >= 5;

  if (props.missedSessionDays >= 2 && isEndOfWeek) {
    alerts.push({
      priority: 3,
      category: "incoherence",
      status: "incomplete",
      label: "Séances manquées",
      message: `${props.missedSessionDays} séances non validées — pense à les mettre à jour avant la fin du cycle`,
    });
  }

  // ── P4: Données manquantes bloquantes ──
  if (!props.hasPlanning) {
    alerts.push({
      priority: 4,
      category: "missing_data",
      status: "incomplete",
      label: "Planning incomplet",
      message: "Aucune séance planifiée cette semaine — configurer ton planning",
    });
  }

  if (props.fatigueScore === null && !props.hasChaos) {
    alerts.push({
      priority: 4,
      category: "missing_data",
      status: "unknown",
      label: "Check-in du jour",
      message: "Pas encore de check-in aujourd'hui — renseigne entraînement, sommeil et nutrition pour activer le suivi",
    });
  }

  // ── P5: Optimisation ──
  // Only show if no higher alerts exist
  if (tab === "programs" && props.weekType && props.programTitle) {
    alerts.push({
      priority: 5,
      category: "optimization",
      status: "ok",
      label: "Cohérence",
      message: "Vérifier la cohérence programme ↔ type de semaine",
    });
  }

  if (tab === "profile" && props.hasPlanning && props.programTitle) {
    alerts.push({
      priority: 5,
      category: "optimization",
      status: "ok",
      label: "Profil à jour",
      message: "Profil à jour — vérifier régulièrement",
    });
  }

  return alerts;
}

// ── Main prioritization function ─────────────────────

export function computePrioritizedAlert(
  props: StatusIndicatorProps
): PrioritizedAlert {
  const alerts = detectAlerts(props);

  if (alerts.length === 0) {
    return {
      priority: 5,
      category: "optimization",
      status: "ok",
      label: "Tout est en ordre",
      message: "Aucune action requise",
      silenced: [],
    };
  }

  // Sort by priority (ascending = most critical first)
  // Failsafe: same priority → stable order (first detected = oldest)
  alerts.sort((a, b) => a.priority - b.priority);

  const primary = alerts[0];
  const silenced: SilencedAlert[] = alerts.slice(1).map((a) => ({
    priority: a.priority,
    category: a.category,
    message: a.message,
  }));

  return {
    priority: primary.priority,
    category: primary.category,
    status: primary.status,
    label: primary.label,
    message: primary.message,
    silenced,
  };
}
